import { useEffect, useMemo, useState } from 'react';
import { sendAppClientDataEvent, useCanEdit, useUiCtxSelector } from '@/core';
import {
  useActiveNavItem,
  useActiveTopNavItem,
  useNavItems,
  useParentNavIds,
  usePrivateNavIds,
} from '@/domains/navs/hooks';
import './secondary-nav.scss';
import clsx from 'clsx';
import {
  layoutSpacing,
  getFlatFromTree,
  getTreeFromFlat,
} from '@knapsack/toby';
import type { KnapsackNavItem, Role } from '@knapsack/types';
import { secondaryNavRootKey } from '@/utils/constants';
import type { NavItem } from '@knapsack/toby';
import { setExpandedNavIds } from './secondary-nav.utils';
import { NavTree } from '../components/nav-tree/nav-tree';

export const SecondaryNav = () => {
  const canEdit = useCanEdit();
  const activeTopNavItem = useActiveTopNavItem();
  const navItems = useNavItems({ includeHiddenItems: false });
  const privateNavIds = usePrivateNavIds();

  const activeNavItem = useActiveNavItem();
  const activeNavItemParentIds = useParentNavIds(activeNavItem?.id);
  const expandedNavIds = useUiCtxSelector((ui) => ui.expandedNavIds);

  const filteredNavItems = useMemo(() => {
    return navItems.filter((item) =>
      [item?.id, item?.parentId].every((id) => !privateNavIds.includes(id)),
    );
  }, [navItems, privateNavIds]);

  const [treeItems, setTreeItems] = useState<NavItem[]>([]);
  const [allTreeItems, setAllTreeItems] = useState<NavItem[]>([]);

  useEffect(() => {
    const initialTreeData = getTreeFromFlat({
      rootKey: activeTopNavItem?.id,
      flatData: filteredNavItems as NavItem[],
      getKey: (item) => item?.id,
      getParentKey: (item) => item?.parentId,
    });

    const treeWithExpanded = setExpandedNavIds({
      items: initialTreeData,
      activeNavItemParentIds,
      activeNavItem,
      expandedIds: expandedNavIds,
    });

    setTreeItems(treeWithExpanded);
  }, [
    activeNavItem,
    activeNavItemParentIds,
    expandedNavIds,
    activeTopNavItem?.id,
    filteredNavItems,
  ]);

  useEffect(() => {
    const fullNavData = getTreeFromFlat({
      flatData: navItems as NavItem[],
      getKey: (item) => item?.id,
      getParentKey: (item) => item?.parentId,
      rootKey: secondaryNavRootKey,
    });

    setAllTreeItems(fullNavData);
  }, [navItems]);

  const handleTreeUpdate = (newTreeItems: NavItem[]) => {
    setTreeItems(newTreeItems);

    const allUpdatedNavItems = allTreeItems.map((item) => {
      if (item.id === activeTopNavItem?.id) {
        item.children = newTreeItems;
      }
      return item;
    });

    const flatData = getFlatFromTree({
      treeData: allUpdatedNavItems,
      getNodeKey: (item) => item.node.id,
      ignoreCollapsed: false,
    });

    const payload = flatData.map((item) => {
      const { node, parentNode } = item;
      const updatedNavItem: KnapsackNavItem = {
        id: node.id,
        name: node.name,
        path: node.path || '',
        parentId: parentNode ? parentNode.id : secondaryNavRootKey,
        hidden: node.hidden,
        minRoleNeeded: node.minRoleNeeded as Role,
      };
      return updatedNavItem;
    });

    sendAppClientDataEvent({
      type: 'navs.secondary.update',
      payload,
    });
  };

  return (
    <div
      className={clsx(
        'ks-test-sidebar',
        layoutSpacing({ paddingBottom: 'small' }),
      )}
    >
      <NavTree
        key={`secondary-nav-${canEdit}`}
        activeNavItem={activeNavItem}
        ariaLabel="Secondary"
        canEdit={canEdit}
        data-testid="secondaryNav"
        handleNewTreeItems={handleTreeUpdate}
        treeItems={treeItems}
      />
    </div>
  );
};

export default SecondaryNav;
